/* The ledger: metric rows × AI / Team / Unknown / Total. Money rows are the part
   Sold in chat (and, in Total only, what was not sold in chat); counts come from
   the sales ledgers (PPVs sold), the AI's counters (PPVs sent, chats, messages)
   and the CRM (the Team's). Unknown has no activity — nobody to count it for. */

import { useTranslation } from 'react-i18next';
import type { ChatStatsResponse, StatisticsResponse } from '../../../api/client';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { aiActivity, teamTotals } from './derive';
import { HATCH, SELLER_COLOR, fmt, ledgerMoney, pct, pick, ratio, share, usd, usd2, type MoneyMode } from './model';

type Cell = string | null; // null renders as a dimmed dash

interface Props {
  stats: StatisticsResponse | null;
  chat: ChatStatsResponse | null;
  mode: MoneyMode;
  hasCrm: boolean;
}

const COLS = '140px repeat(4, minmax(0, 1fr))';

export function SellerLedger({ stats, chat, mode, hasCrm }: Props) {
  const { t } = useTranslation('statsUnified');
  const m = stats?.money;
  const ai = aiActivity(chat?.totals);
  const team = teamTotals(stats?.team ?? [], mode, stats?.team_accounts ?? []);
  const s = (k: 'ai' | 'team' | 'unknown' | 'not_in_chat') => pick(m?.sellers[k], mode);
  const chatTotal = s('ai') + s('team') + s('unknown');
  const sold = m?.ppv_sold;
  const soldN = { ai: sold?.ai.count ?? 0, team: sold?.team.count ?? 0, unknown: sold?.unknown.count ?? 0 };
  const avgSold = (k: 'ai' | 'team' | 'unknown') => (soldN[k] ? usd2(ledgerMoney(ratio(sold?.[k].gross ?? 0, soldN[k]), mode)) : null);
  const allSold = soldN.ai + soldN.team + soldN.unknown;
  const allSoldGross = (sold?.ai.gross ?? 0) + (sold?.team.gross ?? 0) + (sold?.unknown.gross ?? 0);
  const chats = ai.chats + (hasCrm ? team.chats : 0);

  const rows: { label: string; cells: [Cell, Cell, Cell, Cell]; strong?: boolean; tip?: string }[] = [
    { label: t('ledger.soldInChat'), strong: true, tip: t('ledger.soldInChatTip'), cells: [usd(s('ai')), usd(s('team')), usd(s('unknown')), usd(chatTotal)] },
    { label: t('ledger.share'), cells: [`${share(s('ai'), chatTotal)}%`, `${share(s('team'), chatTotal)}%`, `${share(s('unknown'), chatTotal)}%`, '100%'] },
    { label: t('ledger.notInChat'), tip: t('ledger.notInChatTip'), cells: [null, null, null, usd(s('not_in_chat'))] },
    { label: t('ledger.ppvSent'), cells: [fmt(ai.ppvSent), fmt(team.ppvSent), null, fmt(ai.ppvSent + (hasCrm ? team.ppvSent : 0))] },
    { label: t('ledger.ppvSold'), cells: [fmt(soldN.ai), fmt(soldN.team), fmt(soldN.unknown), fmt(allSold)] },
    {
      label: t('ledger.soldRate'),
      tip: t('ledger.soldRateTip'),
      cells: [
        ai.ppvSent ? pct(ratio(soldN.ai, ai.ppvSent) * 100) : null,
        team.ppvSent ? pct(ratio(soldN.team, team.ppvSent) * 100) : null,
        null,
        ai.ppvSent + team.ppvSent ? pct(ratio(soldN.ai + soldN.team, ai.ppvSent + team.ppvSent) * 100) : null,
      ],
    },
    { label: t('ledger.avgSold'), cells: [avgSold('ai'), avgSold('team'), avgSold('unknown'), allSold ? usd2(ledgerMoney(allSoldGross / allSold, mode)) : null] },
    { label: t('ledger.chats'), cells: [fmt(ai.chats), fmt(team.chats), null, fmt(chats)] },
    { label: t('ledger.messages'), cells: [fmt(ai.messages), fmt(team.messages), null, fmt(ai.messages + (hasCrm ? team.messages : 0))] },
    {
      label: t('ledger.revenuePerChat'),
      cells: [
        ai.chats ? usd2(s('ai') / ai.chats) : null,
        team.chats ? usd2(s('team') / team.chats) : null,
        null,
        chats ? usd2((s('ai') + (hasCrm ? s('team') : 0)) / chats) : null,
      ],
    },
  ];

  const head = [
    { key: 'ai', label: t('sellers.ai'), color: SELLER_COLOR.ai },
    { key: 'team', label: t('sellers.team'), color: hasCrm ? SELLER_COLOR.team : 'var(--om-hover)', hatch: !hasCrm },
    { key: 'unknown', label: t('sellers.unknown'), color: SELLER_COLOR.unknown, tip: t('sellers.unknownTip') },
    { key: 'total', label: t('ledger.total') },
  ];

  return (
    <div className="stx-scroll-x" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', minWidth: 0 }}>
      <div style={{ minWidth: 440 }}>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, alignItems: 'center', height: 36, padding: '0 14px', borderBottom: `1px solid ${C.divider}`, font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>
          <span>{t('ledger.metric')}</span>
          {head.map((h) => (
            <span key={h.key} title={h.tip} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, whiteSpace: 'nowrap' }}>
              {h.color && <span style={{ width: 8, height: 8, borderRadius: 2, background: h.color, backgroundImage: h.hatch ? HATCH : undefined }} />}
              {h.label}
            </span>
          ))}
        </div>
        {rows.map((r, ri) => (
          <div key={r.label} style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, alignItems: 'center', height: 36, padding: '0 14px', borderTop: ri ? `1px solid ${C.divider}` : 0, background: r.strong ? 'var(--om-card-2)' : 'transparent' }}>
            <span title={r.tip} style={{ font: f(r.strong ? 600 : 400, 12.5), color: r.strong ? C.text : C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</span>
            {r.cells.map((v, i) => {
              const shown = i === 1 && !hasCrm ? null : v;
              return (
                <span key={i} style={{ textAlign: 'right', font: f(i === 3 || r.strong ? 600 : 400, r.strong ? 13.5 : 12.5), color: shown === null ? C.faint : i === 3 || r.strong ? C.text : 'var(--om-body)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {shown ?? '—'}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
